import { useStoreAppData } from '@state/useGlobalState';
import { useStoreSellTime } from '@state/useStoreSellTime';
import { applyProductData } from '@hooks/config/applyProductData';
import { markUnavailable } from './menuAvailability';

// Every PLU refused since the app opened. The patch is always computed from the pristine
// getDataAuthStore tree (useStoreAppData — in memory only, never patched itself) plus this
// whole set, so a second refusal cannot un-mark the first.
//
// It lives exactly as long as the page: reopening the app downloads a fresh menu on the
// splash screen and starts this set empty. So a mark cannot outlive the session it was
// made in, and a product the operator restocks is orderable again on the next open. Within
// a session it stays unavailable — far better than offering it and refusing it again.
const refusedThisSession = new Set();

/**
 * Applies a sold-out refusal to the menu the customer already has, without re-downloading
 * getDataAuthStore (it carries every product's configuration and can exceed 5 MB): the
 * refused products, and any package containing one, are drawn as sold out from now on.
 *
 * @param refusedItems what /clientTransaction returned: [{ plu_number, plu_name, reason }]
 */
export function flagUnavailableInMenu(refusedItems) {
  (refusedItems ?? []).forEach((item) => {
    if (item?.plu_number) refusedThisSession.add(item.plu_number);
  });

  const products = useStoreAppData.getState().appData?.product;
  if (!Array.isArray(products) || refusedThisSession.size === 0) return;

  const { categories, uuids } = markUnavailable(products, refusedThisSession);
  if (categories !== products) applyProductData(categories);

  // Tiles prefer a sell-time entry over the menu's own flag, and those entries were
  // recorded when the product was added to the cart — while it was still available.
  useStoreSellTime.getState().deactivateSellTimeProducts(uuids);
}
